import { Box, Button, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Typography, CircularProgress, Alert, Snackbar } from "@mui/material";
import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import TradingPartnersDataGrid from "./TradingPartnersDataGrid";
import TradingPartnerKeys from "./TradingPartnerKeys";
import UploadResultsDrawer from "./UploadResultsDrawer";
import { useDispatch, useSelector } from "react-redux";
import { api } from "../../../redux/api";
import NtwAlert from "../../ContextMenu/NewTemplateWizard/NtwContentOptions/NtwAlert";
import CloseIcon from "@mui/icons-material/Close";
import FileIcon from "@mui/icons-material/InsertDriveFile";
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { UploadArea, FilePreview } from "../../Common/StyledUploadComponents";

function TradingPartner({ handleClose, source }) {
  const { t } = useTranslation();
  const { account } = useSelector(state => state.queryParams);
  const dispatch = useDispatch();
  const [enableApply, setEnableApply] = useState(false);
  const [tpKeyScreen, setTPKeyScreen] = useState(false);
  const [tpDetails, setTPDetails] = useState('');
  const [changesApplied, setChangesApplied] = useState(false)
  const [tpName, setTPName] = useState('');
  const [error, setError] = useState('');
  const [severity, setSeverity] = useState('success');
  const [selectedTradingPartner, setSelectedTradingPartner] = useState('')
  const userPermissions = useSelector((state) => state.userPermissions.permissions);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const fileInputRef = useRef(null);
  const [tpRefreshKey, setTpRefreshKey] = useState(0);
  
  // Upload results state
  const [uploadResults, setUploadResults] = useState(null);
  const [resultsDrawerOpen, setResultsDrawerOpen] = useState(false);
  const [uploadResultsAlert, setUploadResultsAlert] = useState({ open: false, message: '', severity: 'success' });
  
  const handleChanges = (value) => {
    setChangesApplied(value)
  }
   
  const handleTPName = (value) => setTPName(value.tradingPartner)
  const applyHandler = async (data) => {
    if(data){
      const response = await dispatch(api.endpoints.updateTradingPartnerDetails.initiate({
        account,
        data
      }))
      if(response?.data?.current_state === 'SUCCESS'){
         setError(t('UPDATE_TP_MSG'));
         setSeverity('success')
         setEnableApply(false);
         setChangesApplied(true);
         setSelectedTradingPartner({tradingPartner: tpName});

      } else if(response?.data?.current_state === 'ERROR'){
        setError(response?.data?.data?.[0] || response?.data?.message || t('BOX_MSG_GENERAL_ERROR'))
        setSeverity('error')
        setChangesApplied(true)
        setEnableApply(false);
        setSelectedTradingPartner({tradingPartner :tpName})

      } else {
        setError(response?.data?.message || t('BOX_MSG_GENERAL_ERROR'))
        setSeverity('error')
        setEnableApply(false);
      }
    }
  };

  const disableApplyButton = (value) => {
    setEnableApply(value);
  };
  const migrateTP = () => {}
  const updataTPDetails = (data) => {
    setTPDetails(data)
  }
  const handleTPKeyScreen = (val) => {
    setTPKeyScreen(val);
    setSelectedTradingPartner({tradingPartner: tpName});
    if(!val) setEnableApply(false)
  }
  
  const handleDownloadTP = async () => {
    try {
      const response = await dispatch(api.endpoints.downloadTradingPartnerCSV.initiate({
        account
      },{
        forceRefetch: true
      }));
      
      if (response?.data) {
        // Create a Blob from the CSV text
        const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
        
        // Create a download link and trigger it
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.href = url;
        link.download = `${account}_tp_download.csv`;
        
        // Trigger download
        document.body.appendChild(link);
        link.click();
        
        // Cleanup
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        
        // Show success message
        setError(t('TP_DOWNLOAD_SUCCESS') || 'Trading Partners downloaded successfully');
        setSeverity('success');
      } else if (response?.error) {
        // Handle error case
        setError(response?.error?.data || t('BOX_MSG_GENERAL_ERROR'));
        setSeverity('error');
      }
    } catch (error) {
      console.error('Download error:', error);
      setError(t('BOX_MSG_GENERAL_ERROR'));
      setSeverity('error');
    }
  }

  const handleFileChange = (e) => {
    const selectedFile = e.target.files && e.target.files[0];
    if (selectedFile) {
      const fileName = selectedFile.name.toLowerCase();
      const fileType = selectedFile.type;
      
      if (fileName.endsWith('.csv') || fileType === 'text/csv' || fileType === 'application/vnd.ms-excel') {
        setUploadFile(selectedFile);
        setUploadError(''); 
      } else {
        setUploadError('Invalid file type. Please upload a CSV file.');
        setUploadFile(null);
      }
    }
  };

  const handleUploadTP = async () => {
    if (!uploadFile) {
      setUploadError('Please select a file first.');
      return;
    }

    setIsUploading(true);
    setUploadError(''); 

    try {
      const formData = new FormData();
      formData.append('file', uploadFile);

      const response = await dispatch(
        api.endpoints.uploadTradingPartnerCSV.initiate({
          account,
          file: formData,
        })
      );

      if (response?.data?.current_state === 'SUCCESS') {
        const data = response?.data?.data;
        
        setUploadResults(data);
        
        // Close upload dialog
        setUploadFile(null);
        // Reset file input value
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        setUploadDialogOpen(false);
        setChangesApplied(true);
        setSelectedTradingPartner('');
        setTPName('');
        setEnableApply(false);
        
        const processed = data.processed_rows || 0;
        const successful = data.successful_updates || 0;
        const skipped = data.skipped_rows || 0;
        const created = data.created_trading_partners || 0;
        
        let message = `${processed} processed, ${successful} updated`;
        if (created > 0) {
          message += `, ${created} created`;
        }
        if (skipped > 0) {
          message += `, ${skipped} skipped`;
        }
        
        setUploadResultsAlert({
          open: true,
          message: message,
          severity: skipped > 0 ? 'warning' : 'success'
        });
        setTpRefreshKey((prev) => prev + 1);
      } else if (response?.data?.current_state === 'ERROR' || response?.data?.current_state === 'FAILED') {
        setUploadError(response?.data?.message || t('BOX_MSG_GENERAL_ERROR'));
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } else if (response?.error) {
        setUploadError(response?.error?.data || response?.error?.message || t('BOX_MSG_GENERAL_ERROR'));
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    } catch (err) {
      console.error('Upload error:', err);
      setUploadError('Upload failed. Please try again.');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } finally {
      setIsUploading(false);
    }
  };

  const resetUploadForm = () => {
    setUploadFile(null);
    setUploadError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  useEffect(() => {
    if (!uploadDialogOpen) {
      resetUploadForm();
    }
  }, [uploadDialogOpen]);

  return (
    <div>
      { tpKeyScreen ? (
        <TradingPartnerKeys setTPKeyScreen={handleTPKeyScreen} tpName={tpName} />
      ) : (
        <div style={{boxSizing:'border-box', height:'100%'}}>
          <div className="font-12 padding-12">{t("TRADING_PARTNER_DESC")}</div>
          <div className="pdc-container" style={{ flexDirection: "column" }} >
             {/* Trading Partner grid section  */}
            <Box style={{ flexGrow: 1, overflow: "hidden"}}>
              <TradingPartnersDataGrid
                setEnableApplyBtn={disableApplyButton}
                setData={updataTPDetails}
                changesApplied={changesApplied}
                setChanges={handleChanges}
                setTPName={handleTPName}
                selectedTradingPartner={selectedTradingPartner}
                refreshTrigger={tpRefreshKey}
              />
            </Box>
            {/* modal actions */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap"
              }}>
              <div style={{ display: "flex", padding: "10px" }}>
                {/* <Button size="small" variant="outlined" onClick={migrateTP}>
                  {t('COPY_TO')}
                </Button> */}
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  gap: "5px",
                  padding: "5px",
                  paddingRight: "10px",
                  flexWrap: "wrap"
                }}>
                {userPermissions.includes("TRADING_PARTNER_RIGHTS") && (
                  <div style={{ display: "flex", gap: "5px" }}>
                    <Button
                      size={"small"}
                      variant="outlined"
                      onClick={() => setUploadDialogOpen(true)}>
                      {t("BTN_UPLOAD_TP")}
                    </Button>
                    <Button
                      size={"small"}
                      variant="outlined"
                      onClick={() => {
                        handleDownloadTP();
                      }}>
                      {t("BTN_DOWNLOAD_TP")}
                    </Button>
                  </div>
                )}
                <Button
                  size={"small"}
                  variant="outlined"
                  onClick={() => setTPKeyScreen(true)}>
                  {t('TRADE_PARTNER_KEYS')}
                </Button>
                <Button
                  size={"small"}
                  variant="outlined"
                  disabled={!enableApply}
                  onClick={() => applyHandler(tpDetails)}>
                  {t('APPLY')}
                </Button>
                {source !== 'repository_management' && <Button size={"small"} variant="outlined" onClick={handleClose}>
                  {t('BTN_CANCEL')}
                </Button>}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Upload Dialog */}
      <Dialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2, overflow: "hidden" },
        }}>
        <DialogTitle
          className="dialog-header"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}>
          <div className="title">{t("BTN_UPLOAD_TP")}</div>
          <IconButton
            edge="end"
            color="inherit"
            onClick={() => setUploadDialogOpen(false)}
            aria-label="close"
            size="small">
            <CloseIcon
              sx={{
                height: "28px",
                width: "28px",
                color: "#777777",
              }}
            />
          </IconButton>
        </DialogTitle>
        <DialogContent className="dialog-content" dividers>
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" gutterBottom sx={{ fontWeight: 600 }}>
              Select a CSV file to upload
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Required format: CSV file with header (account,trading_partner,field_key,field_value)
            </Typography>
          </Box>
          {uploadError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setUploadError('')}>
              {uploadError}
            </Alert>
          )}
          <UploadArea onClick={() => fileInputRef.current.click()}>
            <input
              type="file"
              hidden
              onChange={handleFileChange}
              ref={fileInputRef}
            />
            <CloudUploadIcon color="primary" sx={{ fontSize: 48, mb: 1 }} />
            <Typography variant="body1" gutterBottom>
              {"Click to browse"}
            </Typography>
          </UploadArea>
          {uploadFile && (
            <FilePreview>
              <FileIcon color="primary" sx={{ mr: 1.5 }} />
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="body2" noWrap>
                  {uploadFile.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {(uploadFile.size / 1024).toFixed(2)} KB
                </Typography>
              </Box>
              <IconButton size="small" onClick={resetUploadForm}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </FilePreview>
          )}
        </DialogContent>
        <DialogActions>
          <div>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleUploadTP}
              style={{ marginRight: "10px" }}
              disabled={!uploadFile || isUploading}>
              {isUploading ? <CircularProgress size='16px' /> : "Upload"}
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setUploadDialogOpen(false)}
              style={{ marginRight: "10px" }}
              color="primary">
              {t("CANCEL")}
            </Button>
          </div>
        </DialogActions>
      </Dialog>

      {/* Success/Error Alert */}
      {error && (
        <NtwAlert
          open={Boolean(error)}
          message={error}
          type={severity}
          hideDuration={10000}
          onClose={() => setError("")}
        />
      )}

      {/* Upload Results Alert with View Details Button */}
      <Snackbar
        open={uploadResultsAlert.open}
        autoHideDuration={10000}
        onClose={() => setUploadResultsAlert({ ...uploadResultsAlert, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert
          onClose={() => setUploadResultsAlert({ ...uploadResultsAlert, open: false })}
          severity={uploadResultsAlert.severity}
          variant="filled"
          sx={{
            width: "100%",
            maxWidth: "450px",
            whiteSpace: "normal",
            wordBreak: "break-word",
            display: 'flex',
            alignItems: 'center'
          }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => {
                setResultsDrawerOpen(true);
                setUploadResultsAlert({ ...uploadResultsAlert, open: false });
              }}
              sx={{
                fontWeight: 600,
                textDecoration: 'underline',
                whiteSpace: 'nowrap'
              }}
            >
              View Details
            </Button>
          }
        >
          {uploadResultsAlert.message}
        </Alert>
      </Snackbar>

      {/* Upload Results Drawer */}
      <UploadResultsDrawer
        open={resultsDrawerOpen}
        onClose={() => setResultsDrawerOpen(false)}
        results={uploadResults}
        account={account}
      />
    </div>
  );
}

export default TradingPartner;
